import {
  Component,
  h,
  Element,
  Prop,
  Listen,
  Event,
  type EventEmitter,
} from '@stencil/core';
import { searchOverflowToggler } from '../../utils/helpers/overflowToggler';
import { productNaSelectorWidgetEventEmitter } from './utils';
import { UIaaSProductNaSelector } from './uiaas-product-na-selector';

@Component({
  tag: 'uiaas-product-na-country-selector-widget',
  shadow: true,
  styleUrl: 'uiaas-product-na-country-selector-widget.scss',
})
export class ProductNaCountrySelectorWidget {
  @Prop() readonly contentText: string;
  @Prop() readonly accessibilityMode: boolean;
  @Prop() readonly country: string;
  @Prop() readonly language: string;
  @Prop() readonly locales: string;
  @Prop() readonly tabName: string;
  /*
  * Location of the GA analytics event
  */
  @Prop() readonly location: string;

  @Element() element;

  private removeComponentFromParent() {
      this.element.parentNode.removeChild(this.element);
      searchOverflowToggler(false);
  }

  @Event({
    eventName: 'search:productNaSelector',
  })
    SearchProductNaSelectorEmitterSRP: EventEmitter;

  @Listen('closeButtonClicked', { target: 'window' })
  handleCloseAction() {
      this.removeComponentFromParent();
  }

  @Listen('countryLabelClicked', { target: 'window' })
  handleRedirection(e) {
      const actualLocation = window.location.href;
      const countrySelectorDataArray = e.detail.locale.split('_');

      const sanitizeString = (input: string) => {
          // Allow only specific safe characters for country and language code
          return input.replace(/[^a-zA-Z]/g, '');
      };

      const countrySelectorDataObject: {
        language: string;
        country: string;
      } = {
        language: sanitizeString(countrySelectorDataArray[0] || ''),
        country: sanitizeString(countrySelectorDataArray[1] || ''),
      };

      if (e.detail.consumerName == 'search') {
        const anchorElement = document.createElement('a');
        const newLocation = this.createRedirectionUrl(
          actualLocation,
          countrySelectorDataObject,
        );
        const { country, language } = countrySelectorDataObject;

        const sanitizeUrl = (url: string, country?: string, language?: string) => {
            try {
              const parsedUrl = new URL(url);
              if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
                throw new Error('Invalid protocol');
              }
              return parsedUrl.toString();
            } catch (e) {
            // eslint-disable-next-line no-console
              console.error('Invalid URL detected:', url);
              return `${this.country}/${this.language}/`; // Safe fallback
            }
        };

        anchorElement.target = '_blank';
        anchorElement.href = sanitizeUrl(newLocation, country, language);
        anchorElement.click();
        anchorElement.remove();
        window.URL.revokeObjectURL(newLocation);
      }
  }

  private replaceLocation = (
    url: string,
    countrySelectorDataObject: { language: string; country: string },
  ) => {
      try {
        const parsedUrl = new URL(url);
        // Replace only the path segment, preserving protocol and host
        const newPath = parsedUrl.pathname.replace(
          `${this.country}/${this.language}/`,
          `${countrySelectorDataObject.country}/${countrySelectorDataObject.language}/`,
        );
        parsedUrl.pathname = newPath;
        return parsedUrl.toString();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Invalid URL in replaceLocation:', url);
        return `${this.country}/${this.language}/`; // Safe fallback
      }
  };

  private createRedirectionUrl(
    actualLocation,
    countrySelectorDataObject: { language: string; country: string },
  ) {

      const redirectionUrl = this.replaceLocation(
        actualLocation,
        countrySelectorDataObject,
      );
      productNaSelectorWidgetEventEmitter(
        this.SearchProductNaSelectorEmitterSRP,
        this.location,
        this.tabName,
        'left',
        redirectionUrl,
      );

      return redirectionUrl;
  }

  render() {
      return (
        <div>
          <div class={'smart-country-selector-widget'}>
            <UIaaSProductNaSelector
              title={this.contentText}
              country={this.country}
              language={this.language}
              accessibilityMode={Boolean(this.accessibilityMode)}
              locales={this.locales}
            />
          </div>
          <div
            class={'smart-country-selector-widget__overlay'}
            onClick={() => {
                this.removeComponentFromParent();
                window.dispatchEvent(new CustomEvent('closeButtonClicked'));
            }}
          />
        </div>
      );
  }
}
